import { useState } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import BaseModal from "../shared/components/BaseModal";
import BaseButton from "components/common/BaseButton";
import { usAdminAppRoutes } from "../../../route-definitions";
import ErrorPage from "../shared/components/BaseError";
import EntityPageLayout from "../shared/components/EntityPageLayout";
import useMutateEndUser from "./hooks/useMutateEndUser";
import { EndUserFormData } from "./components/edit-end-user-form/edit-end-user.schema";
import EditEndUserSkeleton from "./components/skeletons/EndUserSkeleton";
import EditEndUserForm from "./components/edit-end-user-form/EditEndUserForm";
import { useEndUser } from "./hooks/useEnduser";

const EditEndUserPage = () => {
  const intl = useIntl();
  const history = useHistory();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const {
    data: endUser,
    isLoading,
    isError,
    error,
    refetch,
  } = useEndUser({ endUserId: entityId });

  const {
    editEndUser,
    editEndUserLoading,
    editEndUserError,
    editEndUserSuccess,
  } = useMutateEndUser({
    id: entityId,
    onEditSuccess: () => {
      setModalIsOpen(true);
    },
    onEditError() {
      setModalIsOpen(true);
    },
  });

  const onSubmit = (data: EndUserFormData) => {
    editEndUser({ ...data, endUserId: entityId });
  };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.editEndUser">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.editEndUser">
        <EditEndUserSkeleton />
      </EntityPageLayout>
    );

  const handleCloseModal = () => {
    setModalIsOpen(false);
    if (editEndUserSuccess) {
      history.push(usAdminAppRoutes.endUsers);
    }
  };

  const endUserError = Array.isArray(editEndUserError?.response?.data?.message)
    ? editEndUserError.response.data.message[0]
    : editEndUserError?.response?.data?.message;

  return (
    <EntityPageLayout headerMessage="usAdmin.editEndUser">
      <BaseModal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        footer={
          <BaseButton
            handleSubmit={handleCloseModal}
            text={intl.formatMessage({ id: "usAdmin.ok" })}
          />
        }
      >
        {editEndUserSuccess && (
          <h2>
            {intl.formatMessage({ id: "usAdmin.endUserUpdatedSuccessfully" })}
          </h2>
        )}
        {editEndUserError && <h2>{endUserError}</h2>}
      </BaseModal>
      <EditEndUserForm
        endUser={endUser!}
        onSubmit={onSubmit}
        isSubmitting={editEndUserLoading}
      />
    </EntityPageLayout>
  );
};

export default EditEndUserPage;
